/**
 * DeviceSetupPage — public page, no user login required.
 *
 * The Android tablet opens the app for the first time and lands here.
 * Staff enters the Device ID (e.g. GATE-A12-001) and a 4-8 digit PIN.
 * On success the device token is stored in localStorage and the tablet
 * is redirected to /device-display which plays announcements automatically.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Radio, Wifi, Volume2, CheckCircle, AlertCircle, Loader } from 'lucide-react';
import { devicesAPI } from '../../services/api';

const DEVICE_TOKEN_KEY = 'device_token';
const DEVICE_INFO_KEY = 'device_info';

export default function DeviceSetupPage() {
  const navigate = useNavigate();
  // Set by DeviceDisplayPage when the admin changed this device's PIN or revoked it
  const signedOutMessage = useLocation().state?.message || '';
  const [deviceId, setDeviceId] = useState('');
  const [pin, setPin] = useState('');
  const [status, setStatus] = useState(signedOutMessage ? 'error' : 'idle'); // idle | loading | success | error
  const [errorMsg, setErrorMsg] = useState(signedOutMessage);

  // If already activated, redirect straight to display
  useEffect(() => {
    const token = localStorage.getItem(DEVICE_TOKEN_KEY);
    if (token) {
      navigate('/device-display', { replace: true });
    }
  }, [navigate]);

  async function handleActivate(e) {
    e.preventDefault();
    if (!deviceId.trim() || !pin.trim()) return;

    setStatus('loading');
    setErrorMsg('');

    try {
      const res = await devicesAPI.activate(deviceId.trim(), pin.trim());
      const data = res.data.data;

      localStorage.setItem(DEVICE_TOKEN_KEY, data.device_token);
      localStorage.setItem(DEVICE_INFO_KEY, JSON.stringify({
        device_id: data.device_id,
        device_name: data.device_name,
        device_type: data.device_type,
        gate_id: data.gate_id,
        zone_code: data.zone_code,
        gate_code: data.gate_code,
        gate_terminal: data.gate_terminal,
      }));

      setStatus('success');
      setTimeout(() => navigate('/device-display', { replace: true }), 1200);
    } catch (err) {
      const msg = err.response?.data?.message || 'Activation failed. Check Device ID and PIN.';
      setErrorMsg(msg);
      setStatus('error');
    }
  }

  return (
    <div className="min-h-screen bg-gray-950 flex flex-col items-center justify-center p-6">
      {/* Logo */}
      <div className="flex items-center gap-3 mb-10">
        <div className="w-14 h-14 rounded-2xl bg-blue-700 flex items-center justify-center">
          <Volume2 className="w-8 h-8 text-white" />
        </div>
        <div>
          <div className="text-white text-2xl font-black tracking-tight">Gate PA System</div>
          <div className="text-gray-500 text-sm">Airport Voice Announcement</div>
        </div>
      </div>

      {/* Card */}
      <div className="w-full max-w-sm bg-gray-900 border border-gray-800 rounded-3xl p-8 shadow-2xl">
        {status === 'success' ? (
          <div className="text-center py-4">
            <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
            <div className="text-white text-xl font-bold">Device Activated!</div>
            <div className="text-gray-400 text-sm mt-2">Starting display…</div>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-3 mb-6">
              <Radio className="w-5 h-5 text-blue-400" />
              <h1 className="text-white text-lg font-semibold">Activate This Device</h1>
            </div>

            <form onSubmit={handleActivate} className="space-y-5">
              <div>
                <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                  Device ID
                </label>
                <input
                  type="text"
                  value={deviceId}
                  onChange={e => setDeviceId(e.target.value.toUpperCase())}
                  placeholder="e.g. GATE-A12-001"
                  autoCapitalize="characters"
                  autoComplete="off"
                  spellCheck={false}
                  className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-white text-lg font-mono placeholder-gray-600 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                  PIN
                </label>
                <input
                  type="password"
                  inputMode="numeric"
                  value={pin}
                  onChange={e => setPin(e.target.value.replace(/\D/g, ''))}
                  placeholder="4–8 digit PIN"
                  maxLength={8}
                  className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-white text-lg tracking-widest placeholder-gray-600 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                />
              </div>

              {status === 'error' && (
                <div className="flex items-start gap-2 bg-red-900/40 border border-red-700 rounded-xl px-4 py-3">
                  <AlertCircle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                  <span className="text-red-300 text-sm">{errorMsg}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={status === 'loading' || !deviceId.trim() || !pin.trim()}
                className="w-full bg-blue-600 hover:bg-blue-500 disabled:bg-gray-700 disabled:text-gray-500 text-white font-bold rounded-xl py-4 text-lg transition-colors flex items-center justify-center gap-2"
              >
                {status === 'loading' ? (
                  <>
                    <Loader className="w-5 h-5 animate-spin" />
                    Activating…
                  </>
                ) : (
                  <>
                    <Wifi className="w-5 h-5" />
                    Activate Device
                  </>
                )}
              </button>
            </form>
          </>
        )}
      </div>

      <p className="text-gray-700 text-xs mt-8 text-center">
        Device ID and PIN are provided by your system administrator
      </p>
    </div>
  );
}
